/**
 * TypeScript interfaces for the compliance scanner
 */

export type ComplianceVerdict = 'PASS' | 'WARN' | 'FAIL';
export type IssueSeverity = 'critical' | 'warning';
export type ScanStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface ComplianceIssue {
  code: string;
  severity: IssueSeverity;
  message: string;
  file?: string;
  remediation?: string;
}

export interface ComplianceSummary {
  total: number;
  passed: number;
  failed: number;
  warnings: number;
}

export interface ScanResult {
  passed: boolean;
  score: number;
  verdict: ComplianceVerdict;
  summary: ComplianceSummary;
  criticalIssues: Array<{
    code: string;
    message: string;
    file?: string;
    remediation?: string;
  }>;
  allIssues: ComplianceIssue[];
}

export interface ScanProgress {
  current: number;
  total: number;
  currentCheck: string;
  percentComplete: number;
}

export interface ScanStatusResponse {
  scanId: string;
  status: ScanStatus;
  progress?: ScanProgress;
  result?: ScanResult;
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface StartScanRequest {
  url: string;
  options?: {
    regions?: string[];
    industry?: string;
    maxPages?: number;
    crawlDepth?: number;
  };
}

export interface StartScanResponse {
  scanId: string;
  status: ScanStatus;
  estimatedTime?: number;
}

export const REGIONS = [
  { value: 'GLOBAL', label: 'Global' },
  { value: 'EU', label: 'European Union' },
  { value: 'US', label: 'United States' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'UAE', label: 'United Arab Emirates' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
] as const;

export const INDUSTRIES = [
  { value: 'general', label: 'General' },
  { value: 'business', label: 'Business/Corporate' },
  { value: 'restaurant', label: 'Restaurant/Food' },
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'fitness', label: 'Fitness/Gym' },
  { value: 'education', label: 'Education' },
  { value: 'travel', label: 'Travel/Tourism' },
  { value: 'legal_finance', label: 'Legal/Finance' },
  { value: 'ecommerce', label: 'Retail/E-commerce' },
  { value: 'beauty', label: 'Beauty/Spa' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'construction', label: 'Construction' },
] as const;
